import React, {useContext, useEffect, useRef, useState, useCallback} from "react";
import StyleContext from "../../contexts/StyleContext";
import {fetchResume, trackResumeEvent} from "../../services/api";
import Loader from "../../components/loader/Loader";
import "./Resume.scss";

// Render bullets / strings safely
const Bullets = ({items = []}) => (
  <ul className="rd-resume__bullets">
    {items.map((b, i) => <li key={i}>{b}</li>)}
  </ul>
);

const Resume = () => {
  const {isDark}      = useContext(StyleContext);
  const [data, setData]       = useState(null);
  const [pdfEnabled, setPdf]  = useState(true);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const docRef = useRef(null);
  const trackedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchResume();   // never throws — handled inside
    setData(res?.resume || null);
    setPdf(res?.pdfEnabled !== false);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fire one view event per page-mount (after data shows).
  useEffect(() => {
    if (!loading && data && !trackedRef.current) {
      trackedRef.current = true;
      trackResumeEvent("view");
    }
  }, [loading, data]);

  // ----- Download as PDF -----
  // Prefer html2pdf.js (true PDF). If the module isn't available
  // (e.g. install hasn't run), fall back to window.print() which
  // honours our @media print A4 stylesheet at the bottom of Resume.scss.
  const downloadPdf = async () => {
    if (!docRef.current) return;
    setDownloading(true);
    trackResumeEvent("download");

    try {
      const mod = await import(/* webpackChunkName: "html2pdf" */ "html2pdf.js");
      const html2pdf = mod.default || mod;
      await html2pdf()
        .set({
          margin:       [10, 10, 10, 10],
          filename:     "Rashid_Ahmad_Resume.pdf",
          image:        {type: "jpeg", quality: 0.98},
          html2canvas:  {scale: 2, useCORS: true, backgroundColor: "#ffffff"},
          jsPDF:        {unit: "mm", format: "a4", orientation: "portrait"},
          pagebreak:    {mode: ["css", "legacy"]},
        })
        .from(docRef.current)
        .save();
    } catch (e) {
      // Graceful fallback — opens the OS print dialog with A4 styles applied.
      console.warn("[resume] html2pdf unavailable, falling back to print()", e?.message);
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <Loader inline label="Loading resume" />;

  if (!data) {
    return (
      <div className="rd-resume__page">
        <p>Resume is temporarily unavailable. Please check back shortly.</p>
      </div>
    );
  }

  const h = data.header || {};

  return (
    <div className={`rd-resume__page ${isDark ? "dark" : ""}`}>
      <div className="rd-resume__toolbar no-print">
        <div>
          <h2 className="rd-resume__title-h2">My Resume</h2>
          <p className="rd-resume__sub">
            A full, formatted version of my CV — feel free to download.
          </p>
        </div>
        {pdfEnabled && (
          <button
            type="button"
            className="rd-resume__dl"
            onClick={downloadPdf}
            disabled={downloading}
          >
            {downloading ? "Preparing PDF…" : "Download as PDF"}
          </button>
        )}
      </div>

      {/* The actual A4 document */}
      <article ref={docRef} className="rd-resume__doc" aria-label="Resume document">
        {/* HEADER */}
        <header className="rd-resume__header">
          <h1>{h.name || "Rashid Ahmad"}</h1>
          {h.title && <p className="rd-resume__role">{h.title}</p>}
          <p className="rd-resume__contact">
            {h.location && <span>{h.location}</span>}
            {h.email && <span><a href={`mailto:${h.email}`}>{h.email}</a></span>}
            {h.phone && <span><a href={`tel:${h.phone}`}>{h.phone}</a></span>}
            {h.linkedin && (
              <span>
                <a href={h.linkedin} target="_blank" rel="noopener noreferrer">
                  {h.linkedinLabel || h.linkedin}
                </a>
              </span>
            )}
          </p>
        </header>

        <hr className="rd-resume__rule" />

        {/* SUMMARY */}
        {data.summary && (
          <section className="rd-resume__section">
            <h2>Professional Summary</h2>
            <p>{data.summary}</p>
          </section>
        )}

        {/* EXPERIENCE */}
        {Array.isArray(data.experience) && data.experience.length > 0 && (
          <section className="rd-resume__section">
            <h2>Work Experience</h2>
            {data.experience.map((x, i) => (
              <div key={i} className="rd-resume__entry">
                <div className="rd-resume__entry-head">
                  <strong>{x.company}</strong>
                  <span className="rd-resume__when">{x.from} – {x.to}</span>
                </div>
                <div className="rd-resume__entry-sub">
                  <em>{x.role}</em>
                  {x.location && <span className="rd-resume__loc">{x.location}</span>}
                </div>
                {Array.isArray(x.bullets) && <Bullets items={x.bullets} />}
              </div>
            ))}
          </section>
        )}

        {/* KEY PROJECTS */}
        {Array.isArray(data.projects) && data.projects.length > 0 && (
          <section className="rd-resume__section">
            <h2>Key Projects</h2>
            {data.projects.map((p, i) => (
              <div key={i} className="rd-resume__entry">
                <div className="rd-resume__entry-head">
                  <strong>{p.name}</strong>
                  {p.period && <span className="rd-resume__when">{p.period}</span>}
                </div>
                {Array.isArray(p.bullets) && <Bullets items={p.bullets} />}
              </div>
            ))}
          </section>
        )}

        {/* EDUCATION */}
        {Array.isArray(data.education) && data.education.length > 0 && (
          <section className="rd-resume__section">
            <h2>Education</h2>
            {data.education.map((e, i) => (
              <div key={i} className="rd-resume__entry">
                <div className="rd-resume__entry-head">
                  <strong>{e.school}</strong>
                  <span className="rd-resume__when">{e.from} – {e.to}</span>
                </div>
                <div className="rd-resume__entry-sub">
                  <em>{e.degree}</em>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* SKILLS */}
        {Array.isArray(data.skills) && data.skills.length > 0 && (
          <section className="rd-resume__section">
            <h2>Skills</h2>
            <div className="rd-resume__chips">
              {data.skills.map((s, i) => (
                <span key={i} className="rd-resume__chip">{s}</span>
              ))}
            </div>
          </section>
        )}

        {/* CERTIFICATIONS */}
        {Array.isArray(data.certifications) && data.certifications.length > 0 && (
          <section className="rd-resume__section">
            <h2>Certifications</h2>
            <ul className="rd-resume__certs">
              {data.certifications.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
};

export default Resume;
